import type { TicketSalesTour } from '@app-types/ticketSales';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { createNestedSlice, type SliceCreator } from '@utils/sliceHelpers';
import { devtoolsInDevelopment } from '../devtoolsInDevelopment';
import type { ImmerStateCreator } from '../immerStateCreator';
import { createTourInformationSlice, type TourInformationStore } from './tourInformation';
import { createVenuesSlice, type VenuesStore } from './venues';

export type TicketingSalesState = {
  ticketingSales: TourInformationStore & VenuesStore & TicketingSalesActions;
};

export interface TicketingSalesActions {
  initializeTicketingSales: (data: TicketSalesTour) => void;
}

const createTicketingSalesInitializationSlice: ImmerStateCreator<TicketingSalesActions> = (_set, get) => ({
  initializeTicketingSales: (data) => {
    const { initializeShows, initializeTourInformation, initializeVenues } =
      get() as TicketingSalesState['ticketingSales'];

    const { venues, ...tourInformation } = data;
    initializeTourInformation(tourInformation);
    initializeVenues(venues);
    venues.forEach((venue) => initializeShows({ venueId: venue.id, shows: venue.shows }));
  },
});

const ticketingSalesSlices: ReadonlyArray<SliceCreator<TicketingSalesState['ticketingSales']>> = [
  createTourInformationSlice,
  createVenuesSlice,
  createTicketingSalesInitializationSlice,
] as const;

export const createTicketingSalesSlice: ImmerStateCreator<TicketingSalesState> = (set, get, store) => ({
  ticketingSales: createNestedSlice(set, get, store, 'ticketingSales', ticketingSalesSlices),
});

export const useTicketingSalesStore = create<TicketingSalesState>()(
  devtoolsInDevelopment(
    immer((...storeCreatorArgs) => ({
      ...createTicketingSalesSlice(...(storeCreatorArgs as Parameters<typeof createTicketingSalesSlice>)),
    })),
    { name: 'TicketingSalesStore' },
  ),
);

export * from './tourInformation';
export * from './venues';
